import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../services/axiosInstance"; // Assuming you have this
import { ImCross } from "react-icons/im";
import { Cancel } from "@mui/icons-material";

const UploadOrderImagePopup = ({
  fetchOrders,
  openPopUp,
  closePopUp,
  orderId,
  orderImageCategory,
  onUploadSuccess,
}) => {
  const [previousImages, setPreviousImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Camera related state (basic placeholders)
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const resetState = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    setLoading(false);
    setError(null);
    setSuccessMessage("");
    setShowCamera(false);
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (openPopUp && orderId) {
      resetState(); // Clear form state, selected files, etc.
      // Fetch previous images if orderId and category are available
      const fetchPreviousImages = async () => {
        if (orderId && orderImageCategory) {
          setLoading(true); // Indicate loading of previous images
          setError(null);
          try {
            const response = await axiosInstance.get(
              `/api/OrderAttachments/order/${orderId}/attachments/${orderImageCategory}`
            );
            const imageBlobUrls = response?.map((a) => ({
              blobUrl: a.blobUrl,
              orderAttachmentId: a.orderAttachmentId,
            }));
            // var imageBlobUrls = response && response.map((a, b) => a.blobUrl);
            setPreviewUrls(imageBlobUrls || []);
          } catch (err) {
            setError(
              err.response?.data?.message ||
                err.response?.data ||
                "Fehler beim Laden früherer Bilder."
            );
            setPreviousImages([]);
          } finally {
            setLoading(false);
          }
        } else {
          setPreviousImages([]);
        }
      };

      fetchPreviousImages();
    } else if (!openPopUp) {
      resetState();
      setPreviousImages([]); // Clear previous images when popup closes
    }
  }, [openPopUp, orderId, orderImageCategory]); // Added orderImageCategory

  const handleFileChange = (e) => {
    setError(null);
    setSuccessMessage("");
    const files = Array.from(e.target.files);
    const newFiles = [];
    const newPreviewUrls = [];

    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit per file
        setError(
          error
            ? `${error}\nFile ${file.name} exceeds 10MB limit.`
            : `File ${file.name} exceeds 10MB limit.`
        );
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError(
          error
            ? `${error}\nFile ${file.name} is not a valid image type.`
            : `File ${file.name} is not a valid image type.`
        );
        return;
      }
      newFiles.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    });

    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setPreviewUrls((prev) => [...prev, ...newPreviewUrls]);
    e.target.value = ""; // Reset file input
  };

  const handleOpenSystemCamera = async () => {
    setShowCamera(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError("Could not access camera. Please check permissions.");
      setShowCamera(false);
    }
  };

  const handleCaptureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        const capturedFile = new File([blob], `capture-${Date.now()}.png`, {
          type: "image/png",
        });
        setSelectedFiles((prev) => [...prev, capturedFile]);
        setPreviewUrls((prev) => [...prev, URL.createObjectURL(capturedFile)]);
      }, "image/png");
      setShowCamera(false); // Optionally close camera after capture
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
    }
  };

  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) {
      setError("Keine neuen Bilder zum Hochladen ausgewählt oder aufgenommen.");
      return;
    }

    // Allowed extensions
    const allowedExtensions = ["png", "jpg", "jpeg"];
    const invalidFiles = selectedFiles.filter((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      return !allowedExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      setError(`Es sind nur PNG- und JPEG-Bilddateien zulässig.`);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccessMessage("");

    const formData = new FormData();
    formData.append("OrderId", orderId);
    formData.append("Category", orderImageCategory);

    selectedFiles.forEach((file) => {
      formData.append("File", file);
    });

    try {
      var response = await axiosInstance.post(
        `/api/OrderAttachments/UploadOrderAttachment`,
        formData
      );
      setSuccessMessage(
        `Successfully uploaded ${selectedFiles.length} image(s).`
      );
      setSelectedFiles([]);
      setPreviewUrls([]);
      if (onUploadSuccess) onUploadSuccess();
      if (response && response.success) {
        setTimeout(closePopUp, 2000);
      }
      fetchOrders();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data ||
          "Fehler beim Hochladen der Bilder. Bitte versuchen Sie es erneut."
      );
    } finally {
      setLoading(false);
      setTimeout(() => {
        handleCancel();
      }, 3000);
    }
  };

  const handleCancel = () => {
    resetState();
    closePopUp();
  };

  const handleDeleteImage = async (imageIdToDelete, orderAttachmentId) => {
    try {
      if (!imageIdToDelete) {
        return;
      }
      setLoading(true);
      setError(null);
      setSuccessMessage("");

      if (imageIdToDelete && orderAttachmentId) {
        await axiosInstance.post(
          `/api/OrderAttachments/DeleteOrderAttachment`,
          {
            imageUrl: imageIdToDelete,
            orderAttachmentId: orderAttachmentId,
          }
        );
      }
      setPreviewUrls((prev) =>
        prev.filter((img) => img.id !== imageIdToDelete)
      );
      setSuccessMessage("Bild erfolgreich gelöscht.");
      if (fetchOrders) fetchOrders();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data ||
          "Fehler beim Löschen des Bildes."
      );
    } finally {
      setTimeout(() => {
        // handleCancel();
      }, 3000);
      setLoading(false);
    }
  };

  if (!openPopUp) return null;

  return (
    <div
      id="AddOrderImageModalContainer"
      className="fixed inset-0 bg-black flex justify-center items-center bg-opacity-20 backdrop-blur-sm z-50" // z-50 to be on top
    >
      <div className="relative bg-white w-11/12 md:w-3/4 lg:w-1/2 xl:max-w-3xl text-center mx-auto rounded-[20px] p-6 md:p-10 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl md:text-2xl font-bold mb-6 text-blue-600">
          Bilder für Auftrag hinzufügen
        </h2>

        {/* Camera View */}
        {showCamera && (
          <div className="mb-4 p-4 border rounded-lg bg-gray-100">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full max-h-60 mb-2 rounded border"
            ></video>
            <canvas ref={canvasRef} className="hidden"></canvas>
            <div className="flex justify-center space-x-2">
              <button
                onClick={handleCaptureImage}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Bild aufnehmen
              </button>
              <button
                onClick={() => {
                  setShowCamera(false);
                  if (videoRef.current && videoRef.current.srcObject) {
                    videoRef.current.srcObject
                      .getTracks()
                      .forEach((track) => track.stop());
                    videoRef.current.srcObject = null;
                  }
                }}
                className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
              >
                Kamera schließen
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons for adding new images */}
        {!showCamera && (
          <div className="mb-6 flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              onClick={handleOpenSystemCamera}
              className="w-full sm:w-auto bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
            >
              {/* Placeholder for Camera Icon */}{" "}
              <span className="mr-2">📷</span> Bild aufnehmen
            </button>
            <label className="w-full sm:w-auto bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer flex items-center justify-center">
              {/* Placeholder for Upload Icon */}{" "}
              <span className="mr-2">📄</span> Dateien hochladen
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          </div>
        )}

        {/* Preview New Images */}
        {previewUrls.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2 text-left text-gray-700">
              Neue Bilder zur Vorschau
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-4 max-h-48 overflow-y-auto p-2 border rounded">
              {previewUrls.map((url, index) => (
                <div key={index} className="relative group">
                  <img
                    src={url?.blobUrl || url}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-40 object-cover shadow"
                  />
                  <button
                    onClick={() => {
                      const updatedPreviews = [...previewUrls];
                      const updatedFiles = [...selectedFiles];
                      updatedPreviews.splice(index, 1);
                      updatedFiles.splice(index, 1);
                      setPreviewUrls(updatedPreviews);
                      setSelectedFiles(updatedFiles);
                      handleDeleteImage(url?.blobUrl, url?.orderAttachmentId);
                    }}
                    className="absolute top-1 right-1 duration-300 z-10 p-1 "
                    title="Bild entfernen"
                  >
                    <div className="bg-white p-1 inline-block rounded-full">
                      <ImCross className="text-black text-base" />
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && <p className="text-blue-500 my-2">Wird hochgeladen...</p>}
        {error && <p className="text-red my-2 whitespace-pre-line">{error}</p>}
        {successMessage && (
          <p className="text-green-500 my-2">{successMessage}</p>
        )}

        {/* Footer Buttons */}
        <div className="flex flex-col sm:flex-row justify-end mt-8 space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            className="bg-gray-200 text-gray-700 uppercase text-sm xl:text-base border border-gray-300 hover:bg-gray-300 font-semibold py-2 px-5 rounded-lg focus:outline-none transition-all ease-in"
            type="button"
            onClick={handleCancel}
          >
            Abbrechen
          </button>
          <button
            className="bg-blue-600 uppercase text-sm xl:text-base border border-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all ease-in"
            type="button"
            onClick={handleUploadImages}
            disabled={loading || selectedFiles.length === 0}
          >
            {loading ? "Wird hochgeladen..." : "Ausgewählte Hochladen"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadOrderImagePopup;
